import { useEffect, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionDocumentsFromClientForEvidence,
  saveOppositionDocumentsFromClientForEvidence,
  type OppositionDocumentsForEvidenceItem,
} from "api/opposition";

export const OPPOSITION_DOCUMENTS_FOR_EVIDENCE_FORM_ID =
  "opposition-documents-for-evidence-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionDocumentsFromClientForEvidenceView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<OppositionDocumentsForEvidenceItem[]>([]);
  const [exhibits, setExhibits] = useState<Record<string, string>>({});
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionDocumentsFromClientForEvidence(leadId)
      .then((data) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        const next: Record<string, string> = {};
        data.conflictingMarks.forEach((m) => {
          next[m.id] =
            m.noOfExhibitsForEvidence != null
              ? String(m.noOfExhibitsForEvidence)
              : "";
        });
        setExhibits(next);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load documents step"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeMark = marks[activeIdx] ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (marks.length === 0) {
      toast.error("No applications on an ACTIVE Evidence invoice yet.");
      return;
    }
    for (const m of marks) {
      const v = exhibits[m.id];
      if (!v || !v.trim()) {
        toast.error(
          `Enter No. of Exhibits for ${m.applicationNo ?? "application"}.`
        );
        return;
      }
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(
          `No. of Exhibits must be a non-negative integer for ${m.applicationNo ?? "application"}.`
        );
        return;
      }
    }
    setSaving(true);
    try {
      await saveOppositionDocumentsFromClientForEvidence(leadId, {
        applications: marks.map((m) => ({
          conflictingMarkId: m.id,
          noOfExhibitsForEvidence: Number(exhibits[m.id]),
        })),
      });
      toast.success("Documents from client saved.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save documents"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No applications are currently on an ACTIVE Evidence invoice. Save
          Step 6 (Invoice to Client for Evidence) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_DOCUMENTS_FOR_EVIDENCE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(m.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>No. of Exhibits for Evidence</label>
          <input
            type="number"
            min={0}
            step={1}
            value={activeMark ? (exhibits[activeMark.id] ?? "") : ""}
            onChange={(e) => {
              if (!activeMark) return;
              setExhibits((prev) => ({
                ...prev,
                [activeMark.id]: e.target.value,
              }));
            }}
            placeholder="Text Here"
            className={INPUT_CLASS}
            disabled={saving}
          />
        </div>
      </div>
    </form>
  );
}
